import { computed, ref } from 'vue'
import { isArray } from '@/global/utils/utils'
import { useTranslate } from '@/global/composables/useTranslate'
import { toVehicleFacts } from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/taskData'

/**
 * Everything a task takes from its vehicle rather than from the user.
 *
 * The vehicle type, the drive, the throw side, the contractor, the base, the base's
 * responsible person, the subcontractor and the operator are all configured on the
 * vehicle - a task only points at a registration, so picking one is what fills them in.
 * The same configuration also bounds the spreading: a task may only spread what the
 * vehicle is set up for, and only as much as its capacity allows.
 *
 * Two things feed this: a task the backend reported (`setFromTask`) and the vehicles
 * autocomplete, which carries the whole configuration on every row (`rememberVehicles`)
 * so that changing the registration needs no second request.
 *
 * Module level, because the fields, the details tab and the create screen are three
 * separate call sites looking at the one task on screen.
 */

const facts = ref(null)

// The configuration of every vehicle the autocomplete has answered with, so a
// registration picked back after being changed away from costs nothing.
const factsByVehicle = ref({})

export function useTaskVehicleFacts () {
  const { translate } = useTranslate()

  /**
   * One autocomplete row as the facts of a vehicle. The endpoint hands the codebook
   * labels over as the translation keys they are kept as, so they are resolved here -
   * the backend does that itself for a task it reports, which is why `toVehicleFacts`
   * translates nothing.
   */
  const rowToFacts = (row) => {
    const label = (value) => value ? translate(value) : null

    return {
      vehicleId: Number(row.id),
      vehicleType: label(row.vehicle_type_description) ?? row.vehicle_type_name ?? null,
      driveType: label(row.drive_type_description) ?? row.drive_type_name ?? null,
      throwSide: label(row.throw_side_description) ?? row.throw_side_name ?? null,
      ownership: label(row.ownership_description) ?? row.ownership_name ?? null,
      // The endpoint concatenates the allowed materials into one column - one row per
      // vehicle is what an autocomplete can work with, and the ids are all that is
      // needed: the spreading codebook is already loaded (see useTaskCodebooks).
      spreadTypeIds: String(row.spread_type_ids ?? '')
        .split(',')
        .map(id => Number(id))
        .filter(id => !!id),
      spreadCapacityMin: row.spread_capacity_min ?? null,
      spreadCapacityMax: row.spread_capacity_max ?? null,
      baseName: row.base_name ?? null,
      baseResponsiblePerson: row.base_responsible_person_name ?? null,
      subcontractorName: row.subcontractor_name ?? null,
      operatorName: row.operator_name ?? null
    }
  }

  /**
   * Remembers what the autocomplete just answered with. Called from the vehicle field's
   * `onDataReceive`, which is the only place the rows are seen - the field itself hands
   * back the picked id and nothing else.
   */
  const rememberVehicles = (rows) => {
    if (!isArray(rows) || !rows.length) {
      return
    }

    const collected = { ...factsByVehicle.value }

    rows.forEach((row) => {
      const id = Number(row?.id)

      if (id) {
        collected[id] = rowToFacts(row)
      }
    })

    factsByVehicle.value = collected
  }

  /**
   * The facts of the task that was just loaded. The task endpoint reports the vehicle's
   * configuration nested under `vehicle`, so nothing has to be fetched for a task that
   * is only being looked at.
   */
  const setFromTask = (record) => {
    const fromTask = toVehicleFacts(record)

    facts.value = fromTask

    if (fromTask?.vehicleId) {
      factsByVehicle.value = {
        ...factsByVehicle.value,
        [fromTask.vehicleId]: fromTask
      }
    }
  }

  const clear = () => {
    facts.value = null
  }

  /**
   * What the picked vehicle is worth as form values. Read-only fields every one of
   * them, so they are shown and never sent - see `toTaskPayload`.
   */
  const factFormValues = computed(() => {
    const current = facts.value

    return {
      vehicle_type: current?.vehicleType ?? null,
      drive_type: current?.driveType ?? null,
      throw_side: current?.throwSide ?? null,
      ownership: current?.ownership ?? null,
      base_name: current?.baseName ?? null,
      base_responsible_person: current?.baseResponsiblePerson ?? null,
      subcontractor_name: current?.subcontractorName ?? null,
      operator_name: current?.operatorName ?? null
    }
  })

  /**
   * A registration the user just picked. Answers with the form values that follow from
   * it, blank when the field was cleared - so the caller can hand them straight to
   * `updateData`.
   */
  const pickVehicle = (vehicleId) => {
    const id = Number(vehicleId)

    facts.value = id ? (factsByVehicle.value[id] ?? null) : null

    return factFormValues.value
  }

  // What the vehicle is allowed to spread. A vehicle with nothing configured puts no
  // limit on the task - the backend refuses only what the vehicle cannot carry.
  const allowedSpreadTypeIds = computed(() => facts.value?.spreadTypeIds ?? [])

  const spreadRange = computed(() => ({
    min: facts.value?.spreadCapacityMin ?? null,
    max: facts.value?.spreadCapacityMax ?? null
  }))

  const hasSpreadRange = computed(() => spreadRange.value.min !== null || spreadRange.value.max !== null)

  /**
   * Whether a quantity fits the picked vehicle. An empty field is nothing to complain
   * about yet - it is `required` that would have to say so, not the range.
   */
  const isSpreadQuantityAllowed = (value) => {
    if (value === null || value === undefined || value === '') {
      return true
    }

    const { min, max } = spreadRange.value
    const quantity = Number(String(value).replace(',', '.'))

    if (Number.isNaN(quantity)) {
      return true
    }

    return (min === null || quantity >= Number(min)) && (max === null || quantity <= Number(max))
  }

  return {
    facts,
    rememberVehicles,
    setFromTask,
    clear,
    pickVehicle,
    factFormValues,
    allowedSpreadTypeIds,
    spreadRange,
    hasSpreadRange,
    isSpreadQuantityAllowed
  }
}
